import { useId } from "react";
import { GROUP_INFO } from "@grundbesitz/shared";
import type { ColorGroup } from "@grundbesitz/shared";
import { relativeLuminance } from "../../lib/boardLayout";

/** Every color group carries its own fine pattern so the group is never told apart by color alone. */
export type PatternKind = "slash" | "dots" | "backslash" | "rows" | "grid" | "columns" | "zigzag" | "checker";

export const GROUP_PATTERN: Record<ColorGroup, PatternKind> = {
  purple: "slash",
  lightblue: "dots",
  pink: "backslash",
  orange: "rows",
  red: "grid",
  yellow: "columns",
  green: "zigzag",
  darkblue: "checker",
};

/** Two-letter codes, shown in the color bar of the board and in the aria label. */
export const GROUP_CODE: Record<ColorGroup, string> = {
  purple: "LI",
  lightblue: "HB",
  pink: "PK",
  orange: "OR",
  red: "RT",
  yellow: "GE",
  green: "GR",
  darkblue: "DB",
};

const TILE = 12;

function overlayColor(group: ColorGroup): string {
  return relativeLuminance(GROUP_INFO[group].color) > 0.3 ? "rgba(30,28,23,0.34)" : "rgba(255,255,255,0.5)";
}

function PatternShapes({ kind, color }: { kind: PatternKind; color: string }) {
  const line = { stroke: color, strokeWidth: 1.8, fill: "none", strokeLinecap: "square" as const };
  switch (kind) {
    case "slash":
      return <path {...line} d="M-2 14 L14 -2 M-4 6 L6 -4 M6 16 L16 6" />;
    case "backslash":
      return <path {...line} d="M-2 -2 L14 14 M-4 6 L6 16 M6 -4 L16 6" />;
    case "dots":
      return (
        <g fill={color}>
          <circle cx="3" cy="3" r="1.9" />
          <circle cx="9" cy="9" r="1.9" />
        </g>
      );
    case "rows":
      return <path {...line} d="M0 3 H12 M0 9 H12" />;
    case "columns":
      return <path {...line} d="M3 0 V12 M9 0 V12" />;
    case "grid":
      return <path {...line} d="M0 6 H12 M6 0 V12" />;
    case "zigzag":
      return <path {...line} strokeLinejoin="round" d="M0 4 L3 8 L6 4 L9 8 L12 4" />;
    case "checker":
      return (
        <g fill={color}>
          <rect x="0" y="0" width="6" height="6" />
          <rect x="6" y="6" width="6" height="6" />
        </g>
      );
  }
}

/** A `<pattern>` (fill color plus the group's mark) to put in a `<defs>` block. Fill with `url(#id)`. */
export function GroupPatternDef({ id, group }: { id: string; group: ColorGroup }) {
  return (
    <pattern id={id} width={TILE} height={TILE} patternUnits="userSpaceOnUse">
      <rect width={TILE} height={TILE} fill={GROUP_INFO[group].color} />
      <PatternShapes kind={GROUP_PATTERN[group]} color={overlayColor(group)} />
    </pattern>
  );
}

/** Small square chip in the group color with its pattern. Decorative: always pair it with the group name. */
export function GroupSwatch({ group, size = 16 }: { group: ColorGroup; size?: number }) {
  const id = `gb-sw-${useId().replace(/:/g, "")}`;
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" className="shrink-0 rounded-[3px]" focusable="false">
      <defs>
        <GroupPatternDef id={id} group={group} />
      </defs>
      <rect width="16" height="16" fill={`url(#${id})`} />
      <rect x="0.5" y="0.5" width="15" height="15" rx="2.5" fill="none" stroke="rgba(30,28,23,0.45)" />
    </svg>
  );
}

/** Color chip plus the group name as text. */
export function GroupBadge({ group, compact = false }: { group: ColorGroup; compact?: boolean }) {
  const info = GROUP_INFO[group];
  if (compact) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-ink">
        <GroupSwatch group={group} size={12} />
        {info.name}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-2.5 py-1 text-sm font-medium text-ink">
      <GroupSwatch group={group} size={16} />
      {info.name}
    </span>
  );
}
